import React from 'react';
import { NextPage } from 'next';
import { getClientIp, getWindow, slugify } from '@surfline/web-common';

import { fetchOSData } from 'actions/deviceData';
import { fetchNearbySpots, fetchSpotDetails, fetchSpotReport, setSpotLoading } from 'actions/spot';
import { fetchOverviewData, setSubregionLoading } from 'actions/subregion';
import SpotGuidePage from 'containers/SpotGuidePage';
import { SL_WEB_LINEUP_CAM_PAYWALL } from 'common/treatments';
import { getOrCreateStore } from 'stores';
import type { SpotReport } from 'types/spot';
import type { WavetrakContext } from 'types/wavetrak';
import getCloudflareCountryCode from 'utils/getCloudflareCountryCode';
import sendRedirect from 'utils/sendRedirect';
import { getServerTreatments } from 'utils/serverSplitClient';
import { AppPageProps } from '../../../_app';

const SpotReportGuidePage: NextPage<AppPageProps> = () => <SpotGuidePage />;

SpotReportGuidePage.getInitialProps = async (context: WavetrakContext) => {
  const store = getOrCreateStore();
  const { req, res, query } = context;
  const isServer = !!req;
  const { dispatch } = store;
  const { spotSlug, spotId } = query as { spotSlug: string; spotId: string };
  const cookies = req?.cookies || {};
  const clientIp = getClientIp(req);

  const countryCode = getCloudflareCountryCode(req);

  const anonymousID = req?.cookies?.ajs_anonymous_id;
  const treatments =
    anonymousID && isServer
      ? await getServerTreatments([SL_WEB_LINEUP_CAM_PAYWALL], null, anonymousID)
      : getWindow()?.__WAVETRAK_TREATMENTS__;
  const includeLineupCams = treatments?.[SL_WEB_LINEUP_CAM_PAYWALL] !== 'off';

  await dispatch(setSpotLoading(true));
  await dispatch(setSubregionLoading(true));
  await Promise.all([
    dispatch(fetchSpotDetails(spotId, cookies)),
    dispatch(fetchNearbySpots(spotId, cookies, clientIp, countryCode, includeLineupCams)),
    dispatch(
      fetchSpotReport(
        { spotId, includeLineupCams },
        (spotReport: SpotReport) =>
          Promise.all([
            dispatch(
              fetchOverviewData(
                spotReport.spot.subregion._id,
                cookies,
                clientIp,
                false,
                countryCode,
              ),
            ),
          ]),
        cookies,
        clientIp,
        countryCode,
      ),
    ),
  ]);
  dispatch(setSubregionLoading(false));
  dispatch(setSpotLoading(false));

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      spotSlug,
      spotId,
    });

    await store.dispatch(fetchOSData(context.req!.headers));
    const state = store.getState();
    const spot = state.spot.report?.data?.spot;
    if (spot && spot._id !== spotId) {
      sendRedirect(res, `../${slugify(spot.name)}/${spot._id}`, 301);
      return {};
    }

    return {
      enableCssBaseline: true,
      enableTheme: true,
      isAppBannerEnabled: true,
      isSpotGuidePage: true,
      ssrReduxState: state,
    };
  }

  return {
    enableCssBaseline: true,
    enableTheme: true,
    isAppBannerEnabled: true,
    isSpotGuidePage: true,
  };
};

export default SpotReportGuidePage;
